import { appDarkPalette, appLightPalette } from './themePalette';

export const authLightTokens = {
  authLayoutBg: appLightPalette.pageBg,
  authCardBg: appLightPalette.surfaceBg,
  authCardBorder: appLightPalette.borderSoft,
  authCardShadow: appLightPalette.shadowSoft,
  authTitleColor: appLightPalette.text,
  authDescriptionColor: appLightPalette.textSecondary,
  authIconColor: appLightPalette.primary,
  authIconBg: `linear-gradient(145deg, ${appLightPalette.primarySoftBg}, ${appLightPalette.primarySoftBgAlt})`,
  authIconBorder: appLightPalette.primaryBorder,
  authIconShadow: 'none',
  authBackColor: appLightPalette.textSecondary,
  authBackBg: appLightPalette.surfaceBgAlt,
  authBackBorder: 'transparent',
  authBackHoverColor: appLightPalette.primary,
  authBackHoverBg: appLightPalette.primarySoftBg,
  authBackHoverBorder: 'transparent',
  authInputColor: appLightPalette.text,
  authInputBg: '#fbfdff',
  authInputBorder: '#d9d9d9',
  authInputPlaceholderColor: '#bfbfbf',
  authInputFocusBorder: appLightPalette.primaryStrong,
  authInputFocusShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
  authInputSuffixColor: appLightPalette.itemText,
  authInputSuffixHoverColor: appLightPalette.primary,
  authCheckboxColor: appLightPalette.itemText,
  authCheckboxBg: appLightPalette.surfaceBg,
  authCheckboxBorder: '#d9d9d9',
  authCheckboxCheckedBg: appLightPalette.primaryStrong,
  authCheckboxCheckedBorder: appLightPalette.primaryStrong,
  authLinkColor: '#1677ff',
  authLinkHoverColor: '#4096ff',
  authPrimaryButtonShadow: '0 10px 24px rgba(37, 99, 235, 0.2)',
  authEmailChipColor: appLightPalette.itemText,
  authEmailChipBg: appLightPalette.surfaceBgAlt,
  authEmailChipBorder: appLightPalette.border,
  authEmailChipShadow: 'none',
  authResendTimerColor: appLightPalette.primaryStrong,
};

export const authDarkTokens = {
  authLayoutBg: appDarkPalette.pageBg,
  authCardBg: appDarkPalette.surfaceBg,
  authCardBorder: appDarkPalette.borderSoft,
  authCardShadow: appDarkPalette.shadowSoft,
  authTitleColor: appDarkPalette.text,
  authDescriptionColor: appDarkPalette.textSecondary,
  authIconColor: '#bfdbfe',
  authIconBg:
    'linear-gradient(145deg, rgba(37, 99, 235, 0.28), rgba(96, 165, 250, 0.16))',
  authIconBorder: appDarkPalette.primaryBorder,
  authIconShadow:
    'inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 12px 28px rgba(37, 99, 235, 0.16)',
  authBackColor: appDarkPalette.itemText,
  authBackBg: appDarkPalette.surfaceBgAlt,
  authBackBorder: 'rgba(148, 163, 184, 0.18)',
  authBackHoverColor: '#bfdbfe',
  authBackHoverBg: appDarkPalette.itemHoverBg,
  authBackHoverBorder: 'rgba(147, 197, 253, 0.28)',
  authInputColor: '#e2e8f0',
  authInputBg: '#0f172a',
  authInputBorder: appDarkPalette.border,
  authInputPlaceholderColor: '#64748b',
  authInputFocusBorder: appDarkPalette.primary,
  authInputFocusShadow: '0 0 0 3px rgba(96, 165, 250, 0.16)',
  authInputSuffixColor: appDarkPalette.textSecondary,
  authInputSuffixHoverColor: '#dbeafe',
  authCheckboxColor: appDarkPalette.itemText,
  authCheckboxBg: '#0f172a',
  authCheckboxBorder: '#475569',
  authCheckboxCheckedBg: appDarkPalette.primaryStrong,
  authCheckboxCheckedBorder: appDarkPalette.primaryStrong,
  authLinkColor: appDarkPalette.primary,
  authLinkHoverColor: '#93c5fd',
  authPrimaryButtonShadow: '0 14px 30px rgba(37, 99, 235, 0.28)',
  authEmailChipColor: '#dbeafe',
  authEmailChipBg: appDarkPalette.surfaceBgAlt,
  authEmailChipBorder: 'rgba(147, 197, 253, 0.22)',
  authEmailChipShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.04)',
  authResendTimerColor: appDarkPalette.primary,
};

const getTokenValue = (token, key, fallback) => token?.[key] || fallback;

export const getAuthCssVariables = (token = {}) => ({
  '--auth-layout-bg': getTokenValue(
    token,
    'authLayoutBg',
    token.colorBgLayout
  ),
  '--auth-card-bg': getTokenValue(token, 'authCardBg', token.colorBgContainer),
  '--auth-card-border': getTokenValue(
    token,
    'authCardBorder',
    token.colorBorderSecondary
  ),
  '--auth-card-shadow': getTokenValue(
    token,
    'authCardShadow',
    token.boxShadowSecondary
  ),
  '--auth-title-color': getTokenValue(
    token,
    'authTitleColor',
    token.colorTextHeading
  ),
  '--auth-description-color': getTokenValue(
    token,
    'authDescriptionColor',
    token.colorTextSecondary
  ),
  '--auth-icon-color': getTokenValue(
    token,
    'authIconColor',
    token.colorPrimary
  ),
  '--auth-icon-bg': getTokenValue(token, 'authIconBg', token.colorPrimaryBg),
  '--auth-icon-border': getTokenValue(
    token,
    'authIconBorder',
    token.colorPrimaryBorder
  ),
  '--auth-icon-shadow': getTokenValue(token, 'authIconShadow', 'none'),
  '--auth-back-color': getTokenValue(
    token,
    'authBackColor',
    token.colorTextSecondary
  ),
  '--auth-back-bg': getTokenValue(token, 'authBackBg', token.colorFillQuaternary),
  '--auth-back-border': getTokenValue(
    token,
    'authBackBorder',
    'transparent'
  ),
  '--auth-back-hover-color': getTokenValue(
    token,
    'authBackHoverColor',
    token.colorPrimary
  ),
  '--auth-back-hover-bg': getTokenValue(
    token,
    'authBackHoverBg',
    token.colorPrimaryBg
  ),
  '--auth-back-hover-border': getTokenValue(
    token,
    'authBackHoverBorder',
    'transparent'
  ),
  '--auth-input-color': getTokenValue(token, 'authInputColor', token.colorText),
  '--auth-input-bg': getTokenValue(token, 'authInputBg', token.colorBgContainer),
  '--auth-input-border': getTokenValue(
    token,
    'authInputBorder',
    token.colorBorder
  ),
  '--auth-input-placeholder-color': getTokenValue(
    token,
    'authInputPlaceholderColor',
    token.colorTextPlaceholder
  ),
  '--auth-input-focus-border': getTokenValue(
    token,
    'authInputFocusBorder',
    token.colorPrimary
  ),
  '--auth-input-focus-shadow': getTokenValue(
    token,
    'authInputFocusShadow',
    token.controlOutline
  ),
  '--auth-input-suffix-color': getTokenValue(
    token,
    'authInputSuffixColor',
    token.colorTextSecondary
  ),
  '--auth-input-suffix-hover-color': getTokenValue(
    token,
    'authInputSuffixHoverColor',
    token.colorPrimary
  ),
  '--auth-checkbox-color': getTokenValue(
    token,
    'authCheckboxColor',
    token.colorText
  ),
  '--auth-checkbox-bg': getTokenValue(
    token,
    'authCheckboxBg',
    token.colorBgContainer
  ),
  '--auth-checkbox-border': getTokenValue(
    token,
    'authCheckboxBorder',
    token.colorBorder
  ),
  '--auth-checkbox-checked-bg': getTokenValue(
    token,
    'authCheckboxCheckedBg',
    token.colorPrimary
  ),
  '--auth-checkbox-checked-border': getTokenValue(
    token,
    'authCheckboxCheckedBorder',
    token.colorPrimary
  ),
  '--auth-link-color': getTokenValue(token, 'authLinkColor', token.colorLink),
  '--auth-link-hover-color': getTokenValue(
    token,
    'authLinkHoverColor',
    token.colorLinkHover
  ),
  '--auth-primary-button-shadow': getTokenValue(
    token,
    'authPrimaryButtonShadow',
    token.boxShadow
  ),
  '--auth-email-chip-color': getTokenValue(
    token,
    'authEmailChipColor',
    token.colorText
  ),
  '--auth-email-chip-bg': getTokenValue(
    token,
    'authEmailChipBg',
    token.colorFillQuaternary
  ),
  '--auth-email-chip-border': getTokenValue(
    token,
    'authEmailChipBorder',
    token.colorBorderSecondary
  ),
  '--auth-email-chip-shadow': getTokenValue(
    token,
    'authEmailChipShadow',
    'none'
  ),
  '--auth-resend-timer-color': getTokenValue(
    token,
    'authResendTimerColor',
    token.colorPrimary
  ),
});
